import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAdmin } from "../../../Context/AdminContext";
import { useAuth, ROLE_HOME } from "../../../Context/AuthContext";
import { Loader } from "../../UI";

/**
 * AdminProtectedRoute — garde de route pour le panel admin.
 *
 * Props :
 *   children      : ReactNode — contenu à afficher si autorisé
 *   requiredRoles : string[]  — rôles ACCEPTÉS (AU MOINS UN suffit) en plus d'être
 *                               authentifié (ex: ['ADMIN_COMPANY','SUPER_ADMIN']).
 *                               Si absent → tout admin authentifié peut accéder.
 *
 * IMPORTANT : Ce composant protège la navigation côté client.
 * La vraie sécurité est toujours backend (middleware auth + requireSuperAdmin).
 */
const AdminProtectedRoute = ({ children, requiredRoles = [], requiredPermission = null }) => {
  const { isAuthenticated, loading, isSuperAdmin, isAdminCompany, can, roles } = useAdmin();
  const auth = useAuth();
  const location = useLocation();
  const { t } = useTranslation('common');

  if (loading) {
    return <Loader size="large" text={t('ui.checkAdmin')} />;
  }

  if (!isAuthenticated) {
    // 🚗 Un CHAUFFEUR / CLIENT déjà connecté qui atterrit sur /admin ne doit PAS
    // être renvoyé vers /admin/login (boucle + appels admin parasites) : on l'envoie
    // directement vers SON espace (ex. /driver/dashboard).
    if (auth.isAuthenticated && auth.role && ROLE_HOME[auth.role] && !ROLE_HOME[auth.role].startsWith('/admin')) {
      return <Navigate to={ROLE_HOME[auth.role]} replace />;
    }
    const safePath = location.pathname + location.search;
    if (safePath.startsWith('/admin/') && !safePath.includes('://')) {
      sessionStorage.setItem("adminRedirectAfterLogin", safePath);
    }
    return <Navigate to="/admin/login" replace />;
  }

  // Autorisation par PERMISSION (prioritaire) : admins → can()=true ; staff → matrice.
  // Permet d'ouvrir une route à un rôle staff précis (ex. Chef d'agence sur /agences)
  // sans la rendre accessible aux autres agents.
  if (requiredPermission) {
    const reqPerms = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    if (!reqPerms.some(p => can(p))) {
      return <Navigate to="/admin" replace state={{ roleError: true, required: requiredPermission }} />;
    }
  } else if (requiredRoles.length > 0) {
    // ✅ Sémantique « AU MOINS UN rôle accepté » (et non « tous »).
    // On teste les rôles RBAC (user_roles) ET le rôle canonique résolu (auth.role) :
    // un compte legacy type_utilisateur='admin' a souvent roles=[] mais auth.role=ADMIN_COMPANY.
    // Sans ce repli, toutes les routes à requiredRoles renvoyaient au Dashboard.
    const effectiveRoles = [
      ...(Array.isArray(roles) ? roles : []),
      ...(auth.role ? [auth.role] : []),
    ];
    const hasRole = isSuperAdmin
      || requiredRoles.some(r => effectiveRoles.includes(r))
      // ADMIN_COMPANY couvre aussi les routes ADMIN_COMPANY même si roles[] est vide.
      || (isAdminCompany && requiredRoles.includes('ADMIN_COMPANY'));
    if (!hasRole) {
      return <Navigate to="/admin" replace state={{ roleError: true, required: requiredRoles }} />;
    }
  }

  return children;
};

export default AdminProtectedRoute;
